import React, { SyntheticEvent, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { setAlert } from "../../../actions/alert";
import { addUser, deleteUser, getAllOrganizations, getUserById, updateUser } from "../../../actions/users";
import { AlertTypes } from "../../../utils/alert.utils";
import { generatePassword } from "../../../utils/password.utils";
import { GenericFormContainer, Spinner, FormChips, Leap4Breadcrumbs } from "@leap4/frontend";
import style from "./style";
import { IOrganization, IUser, User, UserMetadata } from "@leap4/auth";
import * as _ from 'lodash';
import { RootState } from "../../../interfaces/root-state.interface";
import { UsersGState } from "../../../interfaces/user-global-state.interface";
import {
    Box,
    Chip,
    Grid,
    TextField, Tabs, Tab, Button, FormControlLabel, Switch, Tooltip,
    IconButton, InputAdornment,
} from "@mui/material";
import Divider from '@mui/material/Divider';
import AddEditService from "../AddEditService/AddEditService";
import ActivityLogsTab, { ActivityLogEntry } from "./ActivityLogsTab";
import { deleteInstance } from "../../../actions/generic-crud-actions";
import DialogChangePassword from "../../Shared/Dialogs/DialogChangePassword/DialogChangePassword";
import { Mode } from "@leap4/frontend/lib/components/GenericForm/IGenericFormProps";
import TocIcon from "@mui/icons-material/Toc";
import CreateIcon from "@mui/icons-material/Create";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const mapStateToProps = (state: RootState) => ({
    users: state.users
});

const mapDispatch = {
    addUser, updateUser, setAlert, deleteUser, getUserById
};


const connector = connect(mapStateToProps, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;


interface AddEditUserProps extends PropsFromRedux {
    users: UsersGState;
    setAlert: any;
    addUser: any;
    updateUser: any;
    bIsLoading: boolean;
}


interface IFormData extends Partial<IUser> {
    password2?: string;
    lastLoginAt?: Date | string;
    activityLog?: ActivityLogEntry[];
}


const initialUserState: any = {
    email: "",
    password: "",
    organization: "",
    organizations: [],
    name: "",
    password2: "",
    metadata: {},
    metadataMulti: {},
    isActive: true,
    external: false
}


const AddEditUser = ({
    setAlert,
    addUser,
    updateUser,

}: AddEditUserProps): JSX.Element => {
    const history = useHistory();
    const params = useParams<{ id?: string }>();
    const dispatch = useDispatch();
    const [selectedOrganizationTabId, setSelectedOrgTabId] = useState<string>("")
    const [isAddPage, setIsAddPage] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [activeMainTab, setActiveMainTab] = useState<number>(0);
    const [openAddServiceDialog, setOpenAddServiceDialog] = React.useState(false);
    const [modeAddEditServiceDialog, setModeAddEditServiceDialog] = React.useState<boolean>(true);
    const classes = style();


    const [user, setUser] = useState<IFormData>({ ...initialUserState });
    const [optionalOrganizations, setOptionalOrganizations] = useState<Array<IOrganization>>([]);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const [selectedServiceInd, setSelectedServiceInd] = useState<number>(0);

    const [currentServiceMetadata, setCurrentServiceMetadata] = useState<UserMetadata>()
    useEffect(() => {
        setCurrentServiceMetadata(selectedOrganizationTabId && user.metadataMulti![selectedOrganizationTabId])
    }, [selectedOrganizationTabId])

    const updateServiceMetadataOnUser = (serviceName: string, newServiceState: any) => {
        if (!selectedOrganizationTabId) {
            return;
        }

        const currentOrgMetaData = user.metadataMulti?.[selectedOrganizationTabId] || {};

        const newOrgMetadata = {
            ...currentOrgMetaData,
            [serviceName]: newServiceState
        }

        const newUserMetadata = {
            ...(user.metadataMulti || {}),
            [selectedOrganizationTabId]: newOrgMetadata
        }


        setUser({
            ...user, metadataMulti: newUserMetadata
        })

        setCurrentServiceMetadata(prevState => ({ ...newOrgMetadata }))
        setSelectedServiceInd(Object.keys(newOrgMetadata).length - 1);
    }

    const handleFormChange = (e: any) => {
        let { name, value } = e.target;
        setUser({ ...user, [name]: value });
    }

    const handleGeneratePassword = () => {
        const newPassword = generatePassword(16);
        setUser({ ...user, password: newPassword, password2: newPassword });
        setShowPassword(true); // Show the generated password
    }

    const handleCopyPassword = () => {
        if (user.password) {
            navigator.clipboard.writeText(user.password);
            setAlert("Password copied to clipboard!", AlertTypes.SUCCESS);
        }
    }

    const updateUserOrganizations = (organizations: string[]) => {
        console.log(organizations)
        setUser({ ...user, organizations: organizations })
    }

    const handleDeleteService = (serviceName: string) => {
        const currentServiceMDCopy = { ...currentServiceMetadata }
        delete currentServiceMDCopy[serviceName]
        setCurrentServiceMetadata(currentServiceMDCopy)
        selectedOrganizationTabId && (user.metadataMulti![selectedOrganizationTabId] = currentServiceMDCopy)
    }

    const [serviceNameForEdit, setServiceNameForEdit] = useState('')

    const handleEditService = (serviceName: string) => {
        setServiceNameForEdit(serviceName)
        setModeAddEditServiceDialog(false);
        setOpenAddServiceDialog(true);

    }

    const handleSwitchChange = (e: any) => {
        user.external = !user.external
        setCurrentServiceMetadata({})
        setUser(user)
    }


    // Form submit.
    const handleSubmit = (ev: SyntheticEvent) => {
        // For block rendering.
        ev?.preventDefault();
        if (!user) {
            return;
        }

        // Filter metadataMulti to only include organization IDs that exist in user.organizations
        const filteredMetadataMulti = user.metadataMulti && user.organizations ?
            Object.keys(user.metadataMulti).reduce((filtered, orgId) => {
                if (user.organizations?.some(org => org.toString() === orgId)) {
                    filtered[orgId] = user.metadataMulti![orgId];
                }
                return filtered;
            }, {} as any) : user.metadataMulti;

        if (isAddPage) {
            if (user.password !== user.password2) {
                setAlert("Passwords aren't matches.", AlertTypes.FAILURE);
                return;
            }
            const userPayload = {
                ..._.omit(user, ["password2"]),
                metadataMulti: filteredMetadataMulti
            };
            addUser(userPayload, history)
        } else {
            const userPayload = {
                ..._.omit(user, ["password", "password2"]),
                metadataMulti: filteredMetadataMulti
            };
            updateUser(params.id, userPayload, history);
        }
    };


    // Init form.
    useEffect(() => {
        (async () => {
            setIsLoading(true);

            // Fetching all organizations.
            const organizationsData: IOrganization[] = await getAllOrganizations(dispatch);

            // Convert tje organizations objects to ISelectable interface.
            setOptionalOrganizations(organizationsData);
            const id = params.id;
            // For editUser page.
            if (id && id !== "add") {
                setIsAddPage(false);
                const userData: User = await getUserById(id, dispatch);
                _.defaultsDeep(userData, initialUserState);
                setUser({ ...userData });
                const currentOrganization = userData.organization.toString();
                setSelectedOrgTabId(currentOrganization)
            }

            setIsLoading(false);
        })()
    }, []);


    const handleChangeOrgTab = (ev: any, nv: any) => {
        setSelectedOrgTabId(nv)

    }
    // @ts-ignore
    // @ts-ignore
    if (isLoading) return (
        <Spinner />
    )
    return (
        <Grid mt={8}>
            <Leap4Breadcrumbs breadcrumbs={[
                {
                    text: "Users Dashboard",
                    icon: <TocIcon />,
                    link: "/users"
                },
                {
                    text: isAddPage ? "Create" : "Update",
                    link: isAddPage ? "/users/add" : "/users/edit",
                    icon: isAddPage ? <CreateIcon /> : <AutoFixNormalIcon />
                },
                {
                    text: user.name || "New User",
                    link: "",
                    icon: <GpsFixedIcon />
                }
            ]}
            />
            {isLoading ? (
                <Spinner />
            ) : (
                <>
                    {/* Parent Tabs: Configuration / Activity Logs (only show tabs when editing) */}
                    {!isAddPage && (
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                            <Tabs
                                value={activeMainTab}
                                onChange={(e, newValue) => setActiveMainTab(newValue)}
                                aria-label="user tabs"
                            >
                                <Tab label="Configuration" />
                                <Tab label="Activity Logs" />
                            </Tabs>
                        </Box>
                    )}

                    {/* Tab 0: Configuration */}
                    {activeMainTab === 0 && (
                        <GenericFormContainer mode={isAddPage ? Mode.Create : Mode.Edit}
                            disabled={isLoading}
                            onCreateOrEditFunc={(ev: any) => handleSubmit(ev)}
                            onDeleteFunction={() => deleteInstance(params.id as any, "user", history, dispatch)}>
                            <Grid container item alignItems="center" spacing={2} sm={12}>
                                <Grid item xs={5}>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        required
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        value={user?.email}
                                        autoFocus
                                        onChange={handleFormChange}

                                    />
                                </Grid>
                                <Grid item xs={5}>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        required
                                        fullWidth
                                        label="Full name"
                                        name="name"
                                        autoComplete="name"
                                        autoFocus
                                        onChange={handleFormChange}
                                        value={user?.name}
                                    />
                                </Grid>

                                {!isAddPage && params.id &&
                                    <Grid item justifyContent="center" xs={3}>
                                        <DialogChangePassword id={params.id} schemaName={"user"} />
                                    </Grid>
                                }
                                {
                                    // We want to display password fields only in Add user mode.
                                    isAddPage &&
                                    <Grid container item alignItems="center" spacing={1} sm={12}>
                                        <Grid item xs={5}>
                                            <TextField
                                                variant="outlined"
                                                margin="normal"
                                                required
                                                fullWidth
                                                name="password"
                                                label="Password"
                                                type={showPassword ? "text" : "password"}
                                                value={user?.password}
                                                onChange={handleFormChange}
                                                autoComplete="new-password"
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Tooltip title={showPassword ? "Hide password" : "Show password"}>
                                                                <IconButton
                                                                    onClick={() => setShowPassword(!showPassword)}
                                                                    edge="end"
                                                                    size="small"
                                                                >
                                                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Copy password">
                                                                <IconButton
                                                                    onClick={handleCopyPassword}
                                                                    edge="end"
                                                                    size="small"
                                                                    disabled={!user?.password}
                                                                >
                                                                    <ContentCopyIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                variant="outlined"
                                                margin="normal"
                                                required
                                                fullWidth
                                                name="password2"
                                                label="Repeat Password"
                                                type={showPassword ? "text" : "password"}
                                                value={user?.password2}
                                                onChange={handleFormChange}
                                                autoComplete="new-password"
                                            />
                                        </Grid>
                                        <Grid item xs="auto" sx={{ ml: 1 }}>
                                            <Tooltip title="Generate a secure random password">
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={handleGeneratePassword}
                                                    startIcon={<AutorenewIcon />}
                                                    sx={{ mt: 1, height: '56px' }}
                                                >
                                                    Generate
                                                </Button>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                }
                                <Grid item  >
                                    <Tooltip arrow placement={'top'} title={"To change the type, remove and recreate user"}>
                                        <FormControlLabel
                                            control={<Switch color={"error"} disabled={!isAddPage} defaultChecked={user?.external} />}
                                            label="External User"
                                            labelPlacement={'start'}
                                            onChange={handleSwitchChange}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid container item alignItems="center" spacing={2} sm={12}>

                                    <Grid item>
                                        <FormChips optionsList={optionalOrganizations} valueKey={"_id"} labelKey={"name"}
                                            onUpdateItems={updateUserOrganizations}
                                            inputName={"User Organizations"} enableCreate={false}
                                            chips={(user.organizations || []).map(x => x.toString())}
                                            link={(id: string) => `/organizations/${id}`} pureCreate={false} />
                                    </Grid>
                                </Grid>

                                <Grid container sm={12} className={classes.dividerGrid}>
                                    <Divider className={classes.divider}>
                                    </Divider>
                                </Grid>
                                <Grid item container alignItems="flex-start" spacing={2} sm={12} pl={8} gap={3}>
                                    <Tabs
                                        value={selectedOrganizationTabId}
                                        onChange={handleChangeOrgTab}
                                        textColor={"primary"}
                                        indicatorColor={"primary"}
                                        aria-label="secondary tabs example"
                                        sx={{ paddingLeft: 2 }}
                                    >
                                        {

                                            user.organizations.map(orgID => optionalOrganizations.find(x => x._id === orgID))
                                                .map(org => {
                                                    return { name: org?.name || 'NoName', value: org?._id }
                                                }).map(org => {
                                                    return (
                                                        <Tab value={org.value} label={org.name} />
                                                    )

                                                })
                                        }
                                    </Tabs>
                                </Grid>
                                <Grid item width={"100%"}>
                                    <Grid gap={2} container direction="row" spacing={1} pl={5} pr={5} alignItems={'center'}
                                        minHeight={"80px"}>
                                        {
                                            currentServiceMetadata && Object.keys(currentServiceMetadata).map(x => {
                                                return (

                                                    <Chip
                                                        label={`${x}, ${currentServiceMetadata[x].roles[0]}`}
                                                        onClick={() => handleEditService(x)}
                                                        onDelete={() => handleDeleteService(x)}
                                                        color={"info"}
                                                        deleteIcon={<HighlightOffIcon />}
                                                    />
                                                )
                                            })
                                        }
                                    </Grid>

                                </Grid>
                                <Grid item container alignItems="center" sm={4}>
                                    <Button color={'secondary'} variant={'contained'} style={{ minWidth: "240px" }}
                                        onClick={() => {
                                            setModeAddEditServiceDialog(true);
                                            setOpenAddServiceDialog(true);
                                        }}>
                                        Add Service
                                        to {optionalOrganizations.find(org => org._id.toString() === selectedOrganizationTabId)?.name}
                                    </Button>
                                </Grid>

                                <AddEditService isAdd={modeAddEditServiceDialog}
                                    serviceName={serviceNameForEdit}
                                    open={openAddServiceDialog}
                                    setOpen={setOpenAddServiceDialog}
                                    setServiceOnUser={updateServiceMetadataOnUser}
                                    userServices={currentServiceMetadata as UserMetadata}
                                    external={user.external}
                                />
                            </Grid>
                        </GenericFormContainer>
                    )}

                    {/* Tab 1: Activity Logs */}
                    {activeMainTab === 1 && (
                        <ActivityLogsTab
                            lastLoginAt={user.lastLoginAt}
                            activityLog={user.activityLog || []}
                        />
                    )}
                </>
            )}
        </Grid>
    );
}


export default connector(AddEditUser);
